<template>
    <div>
        <div class="mb-3 text-right flex justify-end gap-5">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.adminCP.node_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="handleAddNode">
                {{ $t('labels.nodeModule.addNode') }}
            </hts-form-button>
        </div>
        <hts-data-table
            ref="dataTable"
            view-deleted
            class="ml-2"
            :use-archive="useNodeArchive"
            :use-restore="useNodeRestore"
            use-selection
            :headers="nodeHeader"
            :server="server"
            :pagination="true"
            store-name="nodeDataTable"
            store-key="Node" />
        <add-node
            ref="showDrawerAddNode"
            :size-slide-over="30"
            :node-types="nodeTypes"
            @refresh-tables="refreshTables()" />
        <edit-node
            ref="showDrawerEditNode"
            :node-types="nodeTypes"
            @refresh-tables="refreshTables()" />
        <detail-node
            ref="showDrawerDetailNode"
            :node-types="nodeTypes" />
        <hts-modal
            :show="showPopupDeleteNode"
            :title="$t('labels.nodeModule.archiveNode')"
            type="confirm"
            :content="$t('labels.nodeModule.archiveNodeConfirm')"
            @close="closePopupDeleteNode"
            @confirm="handleDeleteNode(nodeId)" />
        <node-connection ref="nodeConnection" />
    </div>
</template>

<script setup lang="ts">
import { INode, ITollStationInformation, IWarehouseInformation } from './ignore/imports/model'
import NodeConnection from './ignore/node-connection/list.vue'
import layoutPermissions from '~/data/permissions/layout-permissions'
import AddNode from '~/pages/trucker-admin-[id]/node/ignore/add-node.vue'
import DetailNode from '~/pages/trucker-admin-[id]/node/ignore/detail-node.vue'
import EditNode from '~/pages/trucker-admin-[id]/node/ignore/edit-node.vue'

const { $formatters } = useNuxtApp()
const store = $store()

const server: any = ref({} as { [key: string]: string })
const dataTable = ref()
const requestDeleteNode = ref<string | { [key: string]: any }>('')
const showDrawerUpdateRelationNode = ref(false)
const nodeTypes = ref<Array<{ [key: string]: any }>>([])
const showDrawerAddNode: any = ref()
const showDrawerEditNode: any = ref()
const showDrawerDetailNode: any = ref(false)
const showPopupDeleteNode = ref(false)
const nodeConnection = ref()
const viewNodeArchive = ref(false)

const nodeIsSelectedToDelete = reactive<INode>({
  type: '',
  code: '',
  name: '',
  lat: 0,
  lng: 0,
  address: '',
  addressName: '',
  warehouseInformation: {} as IWarehouseInformation,
  tollStationInformation: {} as ITollStationInformation,
  borderGateInformation: {},
  loadLimit: false,
  axes: 0,
  load: 0,
  time: [],
  from: '' as unknown as Date,
  to: '' as unknown as Date,
  description: '',
  position: ''
})

const handleViewDetail = (node: any) => {
  if (!node) {
    return null
  }
  if (showDrawerDetailNode.value) {
    showDrawerDetailNode.value.openEditNode(node._id)
  }
}
const handleEditNode = (node: any) => {
  if (showDrawerEditNode.value) {
    showDrawerEditNode.value.openEditNode(node._id)
  }
}

const handleActionDelete = (node: any) => {
  if (node && node._id) {
    requestDeleteNode.value = {
      selectedItems: [node._id]
    }
  } else {
    requestDeleteNode.value = node
  }
  if (!node) {
    return null
  }
  showPopupDeleteNode.value = true
}
const handleOpenNodeConnection = (node: any) => {
  if (nodeConnection.value) {
    nodeConnection.value.open(node._id)
  }
}

const nodeHeader = computed(() => [
  {
    field: 'location',
    headerName: $t('labels.locationName'),
    minWidth: 150,
    sortable: true,
    filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      return params.data.location?.locationName || ''
    }
  },
  // {
  //  field: 'company.name',
  //  headerName: $t('labels.ownerCompany'),
  //  minWidth: 150,
  //  sortable: true
  // }, ko xoá cmt
  {
    field: 'loadLimit',
    headerName: $t('labels.loadLimit'),
    minWidth: 180,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.hasLoadLimit) {
        return $formatters.formatCurrency(params?.data?.loadLimit) || ''
      }
      return ''
    }
  },
  {
    field: 'nodeType',
    minWidth: 150,
    headerName: $t('labels.nodeType'),
    filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      return params.data.nodeType?.name || ''
    }
  },
  {
    field: 'workingTime',
    minWidth: 150,
    headerName: $t('labels.nodeModule.workingTime'),
    sortable: true,
    valueGetter: (params: any) => {
      const workingTime = params?.data?.workingTime

      const addLabel = (label: string) => $t(`labels.nodeModule.${label}`)
      const addText = (text: string, label: string) => (text ? `${text}, ${label}` : label)

      let text = ''
      if (workingTime) {
        if (checkEnterWorkingTime(workingTime, 'inTime')) {
          text = addText(text, addLabel('inTime'))
        }
        if (checkEnterWorkingTime(workingTime, 'overTime')) {
          text = addText(text, addLabel('overTime'))
        }
        if (checkEnterWorkingTime(workingTime, 'weekend')) {
          text = addText(text, addLabel('weekend'))
        }
      }

      return text
    }
  },
  {
    field: 'workingTime',
    headerName: $t('labels.liftingVehicleReceivingForce'),
    minWidth: 150,
    sortable: true,
    valueGetter: (params: any) => {
      try {
        const liftingVehicleReceivingForce = params?.data?.liftingVehicleReceivingForce

        const addLabel = (label: string) => $t(`labels.${label}`)
        const addText = (text: string, label: string) => (text ? `${text}, ${label}` : label)

        let text = ''
        if (liftingVehicleReceivingForce) {
          if (liftingVehicleReceivingForce.includes('container-truck')) {
            text = addText(text, addLabel('containerTruck'))
          }
          if (liftingVehicleReceivingForce.includes('truck')) {
            text = addText(text, addLabel('truck'))
          }
        }

        return text
      } catch (e) {
        return ''
      }
    }
  },
  {
    field: 'warehouseType',
    headerName: $t('labels.nodeModule.warehouseType'),
    minWidth: 120,
    sortable: true,
    valueGetter: (params: any) => {
      try {
        const warehouseType = params?.data?.warehouseType
        if (!warehouseType || params.data?.nodeType?.name !== 'Kho') {
          return ''
        }

        if (warehouseType === 'BONDED') {
          return $t('labels.nodeModule.bonded')
        }
        if (warehouseType === 'NORMAL') {
          return $t('labels.nodeModule.normal')
        }
      } catch (e) {
        return ''
      }
    }
  },
  {
    field: 'business',
    headerName: $t('labels.businessAndTask'),
    minWidth: 250,
    sortable: true,
    valueGetter: (params: any) => {
      try {
        const businessIds = Object.keys(params?.data?.businesses) || []
        const businesses = store.data.getBusinesses
        const text = businesses
          .filter((business: any) => businessIds?.includes(business._id))
          .map((business: any) => business.name)
          .join(', ')
        return text
      } catch (e) {
        return ''
      }
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    maxWidth: 200,
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    maxWidth: 200,
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    popup: true,
    suppressRowTransform: true,
    suppressMenu: true,
    resizable: false,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: 'Actions',
    cellRendererParams: {
      storeKey: 'nodeDataTable',
      storeName: 'Node',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: true,
          isAll: true,
          click: handleViewDetail
        },
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.adminCP.node_edit),
          click: handleEditNode
        },
        {
          icon: 'hi-eye-icon',
          label: $t('labels.watchNodeConnection'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.adminCP.node_management),
          click: handleOpenNodeConnection
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.adminCP.node_delete),
          isArchive: true,
          click: handleActionDelete
        }
      ]
    }
  }
])

onBeforeMount(() => {
  server.value = { ...$urlAdmin.node.getList }
  getNodeTypesList()
})

const refreshTables = () => {
  if (dataTable.value) {
    dataTable.value.refresh()
  }
}
const handleAddNode = () => {
  if (showDrawerAddNode.value) {
    showDrawerAddNode.value.openAddNode()
  }
}

const getNodeTypesList = () => {
  const api = $urlAdmin.node.getNodeTypes
  $api(api)
    .then((data) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (responseData.success) {
        nodeTypes.value = responseData.data.map((item: any) => {
          item = { ...item, label: $t(item.label) }
          return item
        })
      }
    })
    .catch((error: any) => error)
}

const handleDeleteNode = () => {
  const api = $urlAdmin.node.delete
  $api(api, { body: requestDeleteNode.value })
    .then((data) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (!responseData) {
        return true
      }
      if (responseData.success) {
        refreshTables()
        process.client && $toast().success($t('labels.deleteSuccess'))
        closePopupDeleteNode()
        requestDeleteNode.value = {}
      }
    })
    .catch((error: any) => error)
}

const closePopupDeleteNode = () => {
  showPopupDeleteNode.value = false
  Object.assign(nodeIsSelectedToDelete, {})
}
const handleClose = () => {
  showDrawerUpdateRelationNode.value = false
}

const checkEnterWorkingTime = (workingTime: any, type: string) => {
  try {
    for (const day in workingTime) {
      const dayTimeSlots = workingTime[day]
      for (const timeSlot of dayTimeSlots) {
        if (timeSlot.type === type) {
          return true
        }
      }
    }
    return false
  } catch (e) {
    return false
  }
}

const useNodeArchive = computed(() => {
  return { api: $urlAdmin.node.archive, permissions: checkPermission(layoutPermissions.adminCP.node_archive) }
})
const useNodeRestore = computed(() => {
  return { api: $urlAdmin.node.restore, permissions: checkPermission(layoutPermissions.adminCP.node_archive) }
})
</script>
